import React from 'react'
import { useBetInformers } from '@core/Areas/Racing/Components/BetInformers/Hooks/useBetInformers'
import { BettableTipsPanel, hasTips } from '@core/Areas/Racing/Components/BettableTips'
import { RaceContentPanel } from '@core/Areas/Racing/Components/RaceContent/RaceContentPanel'
import { RacePromotions } from '@core/Areas/Racing/Components/RacePromotion'
import StewardsComments from '@core/Areas/Racing/Components/StewardsComments/StewardsComments'
import { useRacePageData } from '@core/Areas/Racing/Hooks'
import { useRaceContentCardProps } from '@core/Areas/Racing/Hooks/RaceContent/useRaceContentCardProps'
import { useRaceSportsContentCardProps } from '@core/Areas/Racing/Hooks/RaceSportsContent'
import { useRaceStatus } from '@core/Areas/Racing/Hooks/useRaceStatus'
import { type IdentifiableRace, RaceStatus } from '@core/Areas/Racing/Types'
import { useFeature } from '@core/Utils/hooks'
import { PanelsContainerStyled } from '.'

export const RacePanels = (props: IdentifiableRace): JSX.Element | null => {
  const { meetingDate, meetingId, raceNumber } = props
  const isNewPreviewTipsLayoutActive = useFeature('new-preview-tips-layout')
  const { buttons } = useBetInformers()
  const { isLoading, isError, race } = useRacePageData({ meetingId, meetingDate, raceNumber })
  const racecourseSeq = race?.RacecourseSeq || undefined
  const raceCardProps = useRaceContentCardProps(meetingId, meetingDate, raceNumber)
  const sportsCardProps = useRaceSportsContentCardProps(meetingDate, raceNumber, racecourseSeq)
  const raceStatus = race?.MeetingInformation.SelectedRace.RaceStatus as MaybeDefined<RaceStatus>

  const { hasResults, isOpen: isRaceOpen } = useRaceStatus(raceStatus)

  if (isLoading || isError || !race) return null

  const {
    SelectedRace: { Tips: tips, Tipster: tipster, Key: raceKey },
  } = race.MeetingInformation

  if (isNewPreviewTipsLayoutActive) {
    return (
      <PanelsContainerStyled data-testid='race-panels' data-js-race-card-no-swipe>
        {!hasResults && (
          <RacePromotions meetingDate={meetingDate} meetingId={meetingId} raceNumber={raceNumber} />
        )}

        {buttons.tipsVisible &&
          (hasTips(tips, tipster) || !!raceCardProps || !!sportsCardProps) && (
            <BettableTipsPanel
              tips={tips}
              tipster={tipster}
              raceKey={raceKey}
              isRaceOpen={isRaceOpen}
              race={race}
              tipSource='tips'
              meetingId={meetingId}
              meetingDate={meetingDate}
              raceNumber={raceNumber}
              racecourseSeq={racecourseSeq}
            />
          )}

        {buttons.commentsVisible && <StewardsComments {...props} />}
      </PanelsContainerStyled>
    )
  }

  return (
    <PanelsContainerStyled data-testid='race-panels'>
      {!hasResults && (
        <RacePromotions meetingDate={meetingDate} meetingId={meetingId} raceNumber={raceNumber} />
      )}

      <RaceContentPanel
        meetingId={meetingId}
        meetingDate={meetingDate}
        raceNumber={raceNumber}
        raceKey={raceKey}
        isRaceOpen={isRaceOpen}
        race={race}
      />

      {buttons.tipsVisible && hasTips(tips, tipster) && (
        <BettableTipsPanel
          tips={tips}
          tipster={tipster}
          raceKey={raceKey}
          isRaceOpen={isRaceOpen}
          race={race}
          tipSource='tips'
        />
      )}

      {buttons.commentsVisible && <StewardsComments {...props} />}
    </PanelsContainerStyled>
  )
}
